import { ApiError } from '@/api/http';
import { ApiErrorCodes } from '@/api/errors';

/**
 * Why a calendar or contacts request failed, in the words the screen needs.
 *
 * Calendar and contacts used to read every `501` as "not available on this
 * server yet". That was true while the handlers were stubs, and became a lie
 * once they were real: the owner's active mailbox was a *connected* one, the
 * backend refused DAV for connected mailboxes, and the page claimed the whole
 * server had no calendar. The answers these routes can give now mean
 * different things and need different sentences and different buttons:
 *
 *  - `409 stored_credential_invalid` with `account_status: reauth-required` —
 *    the mail host rejected this mailbox's saved password. Only the user can
 *    fix it, from the mailbox's row in Settings → Accounts, which is where the
 *    action goes. A retry cannot succeed, and it is not an ETag conflict
 *    either, although it shares the status.
 *  - `501 unsupported_by_host` — the deployment has no DAV configured for this
 *    mailbox's host. The only honest "not available", and still about the
 *    mailbox rather than the server as a whole.
 *  - `501 not_implemented` with `feature: connected_accounts` — the backend
 *    does not serve this for a connected mailbox (yet).
 *  - any other `501` — a route that is still a stub.
 *  - `502`/`503`/`504`, `429` and network failures — transient: say what went
 *    wrong and offer Try again.
 *
 * `401` is left to the session layer, which already handles it globally; here
 * it is only an ordinary failure with the server's own message.
 */
export type DavFeature = 'calendar' | 'contacts';

export type DavFailureKind =
  | 'reauth'
  | 'unsupported-host'
  | 'connected-unsupported'
  | 'not-built'
  | 'transient'
  | 'failed';

export interface DavFailure {
  kind: DavFailureKind;
  /** One line for a heading, a menu label or a rail note. */
  title: string;
  /** The explanation under the title. */
  detail: string;
  /** Try again can help (transient or unknown failures). */
  retry: boolean;
  /** The only fix is re-entering the mailbox's password. */
  reauth: boolean;
}

const NOUN: Record<DavFeature, { plural: string; Plural: string; service: string }> = {
  calendar: { plural: 'calendars', Plural: 'Calendars', service: 'calendar service' },
  contacts: { plural: 'contacts', Plural: 'Contacts', service: 'address book service' },
};

/** True for the reauth answer a connected mailbox gives once its password is rejected. */
export function isReauthRequired(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return (
    error.is(ApiErrorCodes.StoredCredentialInvalid) ||
    (error.status === 409 && error.details?.['account_status'] === 'reauth-required')
  );
}

/** True for the `501` the backend gives a route it does not serve for connected mailboxes. */
export function isConnectedUnsupported(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 501 &&
    error.details?.['feature'] === 'connected_accounts'
  );
}

export function davFailureKind(error: unknown): DavFailureKind {
  if (!(error instanceof ApiError)) return 'failed';
  if (isReauthRequired(error)) return 'reauth';
  if (error.is(ApiErrorCodes.UnsupportedByHost)) return 'unsupported-host';
  if (isConnectedUnsupported(error)) return 'connected-unsupported';
  if (error.status === 501 || error.is(ApiErrorCodes.NotImplemented) || error.code === 'unsupported_by_server') {
    return 'not-built';
  }
  if (
    error.isOffline ||
    error.isServiceUnavailable ||
    error.isRateLimited ||
    error.status === 502 ||
    error.status === 504 ||
    error.is(ApiErrorCodes.UpstreamBusy) ||
    error.is(ApiErrorCodes.UpstreamTimeout)
  ) {
    return 'transient';
  }
  return 'failed';
}

/**
 * True when the feature cannot work for this mailbox at all, so controls that
 * could only fail (New calendar, New contact, share links) are hidden.
 */
export function isDavUnavailable(error: unknown): boolean {
  const kind = davFailureKind(error);
  return kind === 'unsupported-host' || kind === 'connected-unsupported' || kind === 'not-built';
}

/** The reason half of "Couldn't load … — <reason>". */
function transientReason(error: unknown, service: string): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error && error.message ? error.message : 'something went wrong';
  }
  if (error.isOffline) return 'you appear to be offline';
  if (error.status === 504 || error.is(ApiErrorCodes.UpstreamTimeout)) {
    return `the ${service} did not answer in time`;
  }
  if (error.isRateLimited) return 'too many requests just now; wait a moment';
  if (error.isServiceUnavailable || error.status === 502 || error.is(ApiErrorCodes.UpstreamBusy)) {
    return `the ${service} is unavailable right now`;
  }
  return error.message || 'something went wrong';
}

/**
 * The words for one failure. `email` is the mailbox the request was for; the
 * sentences name it, because "not available" without saying *for whom* is how
 * the owner was told the whole server had no calendar.
 */
export function describeDavFailure(
  feature: DavFeature,
  error: unknown,
  email: string | undefined,
): DavFailure {
  const { plural, Plural, service } = NOUN[feature];
  const who = email && email !== '' ? email : 'this mailbox';
  const kind = davFailureKind(error);
  switch (kind) {
    case 'reauth':
      return {
        kind,
        title: `${who} needs its password again`,
        detail: `The mail host rejected the saved password for ${who}, so its ${plural} can’t be loaded. Enter the password again to reconnect it.`,
        retry: false,
        reauth: true,
      };
    case 'unsupported-host':
      return {
        kind,
        title: `${Plural} aren’t set up for ${who}`,
        detail: `This server has no ${service} configured for the mail host of ${who}, so there is nothing to show. Nothing is broken and nothing was lost.`,
        retry: false,
        reauth: false,
      };
    case 'connected-unsupported':
      return {
        kind,
        title: `${Plural} aren’t available for connected mailboxes yet`,
        detail: `${who} is a connected mailbox, and ${plural} for connected mailboxes aren’t supported yet. Switch to your main mailbox to see its ${plural}.`,
        retry: false,
        reauth: false,
      };
    case 'not-built':
      return {
        kind,
        title: `${Plural} aren’t available for ${who} yet`,
        detail: `The server answered “not implemented” for the ${plural} of ${who}. There is nothing to fix here and nothing to retry.`,
        retry: false,
        reauth: false,
      };
    case 'transient':
    case 'failed':
      return {
        kind,
        title: `Couldn’t load ${plural} for ${who}`,
        detail: `${capitalise(transientReason(error, service))}.`,
        retry: true,
        reauth: false,
      };
  }
}

/**
 * One sentence for a failed *write* (save, delete): what the user needs to
 * know about the mailbox, or the server's own message for anything else.
 */
export function davWriteFailureSentence(
  feature: DavFeature,
  error: unknown,
  email: string | undefined,
): string | undefined {
  const described = describeDavFailure(feature, error, email);
  switch (described.kind) {
    case 'reauth':
      return `${described.title} before its ${NOUN[feature].plural} can be changed.`;
    case 'unsupported-host':
    case 'connected-unsupported':
    case 'not-built':
      return `${described.title}.`;
    default:
      return undefined;
  }
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
